/**
 * Who may use the app on a phone, and what counts as a phone.
 *
 * Split out of MobileGate so the rule is testable. The component reads the
 * viewport and renders; every decision is here.
 */
import { isCompanyAdmin } from '../store/auth';
import type { User } from '../types';

/** Phones are at or below this width. Tablets and laptops (>=768px) are not. */
export const PHONE_MAX_WIDTH = 767;

/**
 * ...and a phone on its side is at or below this height.
 *
 * A width test alone has a hole: an iPhone 14 in landscape is 844×390, so it
 * clears a 767px width bound and the block never fires. That was tolerable
 * while the block was "this layout will be cramped" — somebody who rotated
 * their phone to get in had only a bad experience to gain. It stops being
 * tolerable once the block encodes a decision about *who* may use mobile,
 * because rotating the handset then bypasses the rule.
 *
 * 480 rather than something closer to a phone's ~390-430: high enough to catch
 * every handset in landscape, low enough that a laptop window would have to be
 * shorter than any real one to trip it. It is still a heuristic about hardware
 * inferred from a viewport, and it is not a security control — see
 * shouldBlockMobile.
 */
export const PHONE_MAX_LANDSCAPE_HEIGHT = 480;

export function isPhoneViewport(width: number, height: number): boolean {
  if (width <= PHONE_MAX_WIDTH) return true;
  // Wider than a phone but shorter than any laptop, and wider than it is tall:
  // a handset held sideways.
  return height <= PHONE_MAX_LANDSCAPE_HEIGHT && width > height;
}

/**
 * Whether to show the block instead of the app.
 *
 * Three rules, in order:
 *
 * 1. **Not a phone — never blocked.** Nothing about a desktop changes.
 * 2. **Nobody signed in — never blocked.** The gate used to wrap the login
 *    page, so it refused before it knew who was asking. A rule about roles
 *    cannot be applied to somebody who has not identified themselves yet, and
 *    an owner has to be able to reach the login form on their phone in order
 *    to become an owner.
 * 3. **Otherwise, owners and company admins only.**
 *
 * This is a UX gate and not a control. It runs in the browser, so anybody who
 * resizes a window or opens dev tools is through it, and the API neither knows
 * nor cares what size screen a request came from. It exists so that people who
 * would get a broken experience are told why, not to keep anybody out of
 * anything.
 */
export function shouldBlockMobile(
  user: User | null,
  width: number,
  height: number,
): boolean {
  if (!isPhoneViewport(width, height)) return false;
  if (user === null) return false;
  return !isCompanyAdmin(user);
}
